
import React from "react";
import { useNavigate } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import PreloadLink from "../navigation/PreloadLink";

// Helper function to detect match for current path, including hashes for anchor links
function isActive(item, currentPath) {
  if (!item) return false;
  
  // For items with direct path
  if (item.to) {
    // Exact match
    if (currentPath === item.to) return true;
    
    // Check if it's a sub-path match (but not for the root path)
    if (item.to !== "/" && currentPath.startsWith(item.to)) {
      // Special case for service pages - require exact match of service ID
      if (item.to.includes('/services/')) {
        return currentPath === item.to;
      }
      // For other pages, allow subpath matching
      return true;
    }
    return false;
  }
  
  // For items with children, check if any child matches exactly
  if (item.children) {
    return item.children.some(child => {
      if (currentPath === child.to) return true;
      // For service pages in dropdown, ensure exact matching
      if (child.to.includes('/services/')) {
        return currentPath === child.to;
      }
      // For other paths, check if it's a parent path
      return child.to !== "/" && currentPath.startsWith(child.to);
    });
  }
  
  return false;
}

const NavbarDropdownItem = ({ item, openPopover, setOpenPopover, currentPath }) => {
  const navigate = useNavigate();
  const isTopActive = isActive(item, currentPath);

  if (!item.children) {
    return (
      <PreloadLink
        to={item.to}
        className={`flex items-center text-white transition-colors duration-200 font-bold px-4 -mx-4 py-2.5
          ${isTopActive ? "bg-gray-800" : "hover:bg-gray-800"}`}
        scrollToTop={true}
        aria-current={isTopActive ? "page" : undefined}
      >
        {item.title}
      </PreloadLink>
    );
  }

  return (
    <Popover open={openPopover === item.title} onOpenChange={(open) => setOpenPopover(open ? item.title : null)}>
      <PopoverTrigger asChild>
        <button
          className={`flex items-center text-white focus:outline-none transition-colors duration-200 font-bold px-4 -mx-4 py-2.5
            ${isTopActive ? "bg-gray-800" : "hover:bg-gray-800"}`}
          onMouseEnter={() => setOpenPopover(item.title)}
          onMouseLeave={() => setOpenPopover(null)}
          aria-current={isTopActive ? "page" : undefined}
        >
          {item.title}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 bg-orange-400 shadow-lg rounded-none border-0 absolute w-auto min-w-[160px]" // Increased min-width
        onMouseEnter={() => setOpenPopover(item.title)}
        onMouseLeave={() => setOpenPopover(null)}
        sideOffset={0}
        align="start"
        style={{
          marginTop: '0',
          transform: 'translateY(0)',
        }}
      >
        <ul className="py-0 font-alatsi">
          {item.children.map((child) => {
            // Ensure exact path matching for service pages
            const childActive = currentPath === child.to;
            
            return (
              <li key={child.title}>
                <PreloadLink
                  to={child.to}
                  className={`block px-3 py-2 text-white transition-colors duration-200 font-bold first:pt-3 last:pb-3 
                    ${childActive ? "bg-gray-800" : "hover:bg-gray-800"} 
                    whitespace-nowrap text-left`}
                  scrollToTop={!child.to.includes('#')}
                  aria-current={childActive ? "page" : undefined}
                >
                  {child.title}
                </PreloadLink>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
};

export default NavbarDropdownItem;
